import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { useToast } from "../../hooks/use-toast";
import { authInterceptor } from "../../lib/auth-interceptor";
import { ESTIMATE_UNIT_OPTIONS, laborMethodForEstimateUnit } from "../../lib/estimate-units";

export type CreatedServicePayload = {
  serviceType: string;
  laborRate: number;
  /** Human-readable name as entered (for line description / labels) */
  displayName?: string;
  /** Unit chosen in the dialog (e.g. sqft, hour) */
  unit?: string;
};

export interface AddServiceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called after the service is saved; use serviceType + laborRate on the active line item */
  onCreated: (payload: CreatedServicePayload) => void;
}

export function AddServiceDialog({ open, onOpenChange, onCreated }: AddServiceDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [unit, setUnit] = useState("unit");
  const [labor, setLabor] = useState("0");
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setName("");
    setUnit("unit");
    setLabor("0");
  };

  const handleSave = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      toast({
        title: "Name required",
        description: "Enter a name for the service.",
        variant: "destructive",
      });
      return;
    }
    setSaving(true);
    try {
      const slugBase = trimmed
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "");
      const serviceType = slugBase || `custom-${Date.now()}`;
      const laborRate = parseFloat(labor) || 0;
      const res = await fetch("/api/direct/services", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authInterceptor.addAuthHeaders() },
        body: JSON.stringify({
          name: trimmed,
          serviceType,
          unit,
          laborRate,
          laborMethod: laborMethodForEstimateUnit(unit),
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || err.error || "Could not create service");
      }
      const created = await res.json();
      await queryClient.invalidateQueries({ queryKey: ["/api/direct/services"] });
      const st = (created.service_type as string) ?? serviceType;
      const lr = parseFloat(String(created.labor_rate ?? created.laborRate ?? laborRate)) || laborRate;
      onCreated({ serviceType: st, laborRate: lr, displayName: trimmed, unit });
      onOpenChange(false);
      reset();
      toast({
        title: "Service added",
        description: `"${trimmed}" is in your list and selected for this line.`,
      });
    } catch (e: unknown) {
      toast({
        title: "Could not add service",
        description: e instanceof Error ? e.message : "Try again or add services under Pricing.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) reset();
        onOpenChange(v);
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add a service</DialogTitle>
          <DialogDescription>
            Saves to your account (same list as Pricing). You can edit rates later under Pricing settings.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="add-svc-name">Service name</Label>
            <Input
              id="add-svc-name"
              placeholder="e.g. Gutter cleaning"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="border-slate-200"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Unit</Label>
            <Select value={unit} onValueChange={setUnit}>
              <SelectTrigger className="border-slate-200">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="max-h-[280px]">
                {ESTIMATE_UNIT_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="add-svc-labor">Default labor rate ($)</Label>
            <Input
              id="add-svc-labor"
              type="number"
              min="0"
              step="0.01"
              value={labor}
              onChange={(e) => setLabor(e.target.value)}
              className="border-slate-200"
            />
            <p className="text-xs text-muted-foreground">
              Suggested line price for this item; you can still change it below.
            </p>
          </div>
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button type="button" onClick={() => void handleSave()} disabled={saving} className="gap-2">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Save &amp; use
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
